import { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ChevronDown, FileText, ListFilter } from "lucide-react";
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";
import { getTreeDashboard } from "lib/api/projectStructure";
import { projectNodePaths } from "lib/utils/projectNodePaths";
import { dateLabel, fileHref, METRICS, metricText, previousChange, recordTime, RESULT_LABELS, resultCode, resultLabel, sortRecords } from "lib/utils/treeDashboardRecords";
import "css/treeNodeDashboard.css";

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

const scopeKeys = ["treeBuildingName", "treeFloor", "treeLocation", "treeFacilityName", "treePartName"];
const pathKeys = ["building_name", "floor", "location", "facility_name", "part_name"];

function Result({ file }) {
  return <span className={`nodeDashboard__result is-${file ? resultCode(file) ?? "unknown" : "empty"}`}>{resultLabel(file)}</span>;
}

function NodeDetails({ storageKey, children }) {
  const detailsId = useId();
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(storageKey) === "true"; }
    catch { return false; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, String(next)); }
    catch { /* 현재 화면의 펼침 상태는 유지한다. */ }
  };
  return <div className="treeDashboard__nodeDetails">
    <button type="button" className="treeDashboard__expand" aria-expanded={open} aria-controls={detailsId} onClick={toggle}>
      {open ? "상세 내용 접기" : "자세히 보기"}<ChevronDown size={16} aria-hidden="true" />
    </button>
    <div id={detailsId} className="treeDashboard__nodeDetailsContent" hidden={!open}>{open && children}</div>
  </div>;
}

function FacilityRecords({ identity, projectId, node, nodes, records, detailsStorageKey }) {
  const paths = useMemo(() => projectNodePaths(nodes), [nodes]);
  const parts = nodes.filter(item => item.node_type === "part" && item.parent_id === node.id);
  const byPart = new Map(parts.map(part => [part.id, []]));
  records.forEach(file => byPart.get(file.part_id)?.push(file));
  const rows = parts.map(part => {
    const files = byPart.get(part.id), latest = [...files].reverse().find(file => recordTime(file).time != null);
    const previous = latest && resultCode(latest) == null ? [...files].reverse().find(file => resultCode(file) != null) : null;
    return { part, files, latest, previous };
  });
  const stats = [
    ["등록 부품", rows.length], ["측정 이력 있음", rows.filter(row => row.files.length).length],
    ...RESULT_LABELS.map((label, code) => [label, rows.filter(row => row.latest && resultCode(row.latest) === code).length]),
    ["판정 없음", rows.filter(row => row.files.length && resultCode(row.latest) == null).length],
    ["측정 이력 없음", rows.filter(row => !row.files.length).length],
  ];
  const unmatched = records.filter(file => !byPart.has(file.part_id)).length;
  const partHref = part => {
    const path = paths.find(item => item.id === part.id);
    const params = new URLSearchParams({ treeNodeId: part.id });
    pathKeys.forEach((key, index) => { if (path?.[key]) params.set(scopeKeys[index], path[key]); });
    return `/project/${encodeURIComponent(projectId)}?${params}`;
  };
  return <>
    <div className="treeDashboard__summaryRow">
      {identity}
      <div className="treeDashboard__summaryGroup"><div className="nodeDashboard nodeDashboard--facility">
    <dl className="nodeDashboard__stats">{stats.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value.toLocaleString()}<small>개</small></dd></div>)}</dl>
      </div></div>
    </div>
    <NodeDetails storageKey={detailsStorageKey}>
      <div className="nodeDashboard nodeDashboard__tableWrap"><table>
        <caption className="nodeDashboard__srOnly">{node.name} 부품별 최근 측정 기록</caption>
        <thead><tr><th scope="col">부품</th><th scope="col">최근 측정일</th><th scope="col">최근 파일 진단 결과</th><th scope="col">측정 기록 수</th><th scope="col">최근 파일</th></tr></thead>
        <tbody>{rows.map(({ part, files, latest, previous }) => <tr key={part.id}>
          <th scope="row"><Link to={partHref(part)}>{part.name}</Link></th>
          <td>{latest ? dateLabel(latest) : files.length ? "날짜 미상 · 최신 기록 확인 불가" : "측정 이력 없음"}</td><td><Result file={latest || (files.length ? {} : null)} />{previous && <small className="nodeDashboard__previous">이전: {resultLabel(previous)}<br />{dateLabel(previous)}</small>}</td>
          <td>{files.length.toLocaleString()}개</td>
          <td>{fileHref(projectId, latest) ? <div className="nodeDashboard__links"><Link to={fileHref(projectId, latest)}>파일 보기</Link></div> : "—"}</td>
        </tr>)}</tbody>
      </table></div>
      {!rows.length && <p className="nodeDashboard__empty">등록된 부품이 없습니다. 프로젝트 설정에서 부품을 등록하세요.</p>}
      {unmatched > 0 && <small className="nodeDashboard__excluded">부품 미지정 기록 {unmatched.toLocaleString()}개 · 부품 통계 제외</small>}
    </NodeDetails>
  </>;
}

function TrendChart({ title, records, metrics, selectedId, onSelect }) {
  const dated = records.filter(file => recordTime(file).time != null);
  const singleTime = dated.length === 1 ? recordTime(dated[0]).time : null;
  const hasValues = dated.some(file => metrics.some(metric => metric.value(file) != null));
  const datasets = metrics.map((metric, index) => ({
    label: `${metric.label} (${metric.unit})`,
    data: dated.map(file => ({ x: recordTime(file).time, y: metric.value(file), uploadId: file.upload_id })),
    borderColor: metric.color, backgroundColor: metric.color, borderWidth: 2,
    borderDash: index ? [index * 3, 3] : [], pointStyle: ["circle", "triangle", "rect", "rectRot"][index],
    pointRadius: dated.map(file => file.upload_id === selectedId ? 4 : dated.length === 1 ? 3 : 0), pointHitRadius: 12,
    tension: 0, spanGaps: false,
  }));
  const options = {
    responsive: true, maintainAspectRatio: false, animation: false,
    interaction: { mode: "nearest", intersect: false },
    onClick: (_event, elements) => { if (elements.length) onSelect(dated[elements[0].index].upload_id); },
    plugins: { legend: { display: metrics.length > 1, position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } }, tooltip: { callbacks: {
      title: items => dateLabel(dated[items[0]?.dataIndex]),
      label: item => `${metrics[item.datasetIndex].label}: ${metricText(item.parsed.y, metrics[item.datasetIndex].unit)}`,
    } } },
    scales: {
      x: { type: "linear", min: singleTime != null ? singleTime - 86400000 : dated.length > 1 ? recordTime(dated[0]).time : undefined,
        max: singleTime != null ? singleTime + 86400000 : dated.length > 1 ? recordTime(dated[dated.length - 1]).time : undefined,
        ticks: { display: true, maxTicksLimit: 3, maxRotation: 0, color: "#64748b", font: { size: 10 }, callback: (value, index, ticks) => singleTime != null
          ? index === Math.floor(ticks.length / 2) ? new Date(singleTime).toLocaleDateString("ko-KR", { timeZone: "Asia/Seoul", month: "numeric", day: "numeric" }) : ""
          : new Date(value).toLocaleDateString("ko-KR", { timeZone: "Asia/Seoul", month: "numeric", day: "numeric" }) },
        grid: { display: true, color: "#e2e8f0", lineWidth: 1 }, border: { display: true, color: "#94a3b8" } },
      y: { ticks: { display: true, maxTicksLimit: 4, color: "#64748b", font: { size: 10 } },
        grid: { display: true, color: "#e2e8f0", lineWidth: 1 }, border: { display: true, color: "#94a3b8" } },
    },
  };
  return <section className="nodeDashboard__chart"><h4>{title}</h4>
    {hasValues ? <div className="nodeDashboard__canvas"><Line data={{ datasets }} options={options} role="img" aria-label={`${title}. 측정 기록 선택 메뉴로도 기록과 값을 확인할 수 있습니다.`} /></div>
      : <p className="nodeDashboard__empty">날짜와 측정값이 있는 기록이 없습니다.</p>}
  </section>;
}

function PartRecords({ identity, projectId, records, detailsStorageKey }) {
  const [selection, setSelection] = useState(null);
  const [pointIds, setPointIds] = useState([]);
  const latest = records[records.length - 1];
  const current = records.find(file => file.upload_id === selection) || latest;
  const isLatest = current?.upload_id === latest?.upload_id;
  const availablePoints = METRICS.slice(1, 4).filter(metric => records.some(file => metric.value(file) != null));
  const shown = [METRICS[0], ...availablePoints.filter(metric => pointIds.includes(metric.id)),
    ...(records.some(file => METRICS[4].value(file) != null) ? [METRICS[4]] : []), METRICS[5]];
  if (!current) return <div className="treeDashboard__summaryRow">
    {identity}
    <div className="treeDashboard__summaryGroup"><div className="nodeDashboard">
      <p className="nodeDashboard__empty">측정 이력이 없습니다. 이 부품에 파일을 업로드하면 측정 추이를 확인할 수 있습니다.</p>
    </div></div>
  </div>;
  return <>
    <div className="treeDashboard__summaryRow">
      {identity}
      <div className="treeDashboard__summaryGroup"><div className="nodeDashboard nodeDashboard--part">
    <div className="nodeDashboard__trends">
        {availablePoints.length > 0 && <fieldset className="nodeDashboard__series"><legend>온도 비교</legend>{availablePoints.map(metric => <label key={metric.id}>
          <input type="checkbox" checked={pointIds.includes(metric.id)} onChange={event => setPointIds(ids => event.target.checked ? [...ids, metric.id] : ids.filter(id => id !== metric.id))} />{metric.label}
        </label>)}</fieldset>}
        <div className="nodeDashboard__trendGrid">
          <TrendChart title="센터 온도 · ℃" records={records} metrics={[METRICS[0], ...availablePoints.filter(metric => pointIds.includes(metric.id))]} selectedId={current.upload_id} onSelect={setSelection} />
          {shown.includes(METRICS[4]) && <TrendChart title="3점 온도차 · ℃" records={records} metrics={[METRICS[4]]} selectedId={current.upload_id} onSelect={setSelection} />}
          <TrendChart title="평균 초음파 · dB" records={records} metrics={[METRICS[5]]} selectedId={current.upload_id} onSelect={setSelection} />
        </div>
    </div>
      </div></div>
    </div>
    <NodeDetails storageKey={detailsStorageKey}>
      <section className="nodeDashboard nodeDashboard__record" aria-label="선택한 측정 기록">
        <div className="nodeDashboard__recordHeader">
          <div className="nodeDashboard__recordMeta">
            <div className="nodeDashboard__recordLabel"><span>측정일</span>{isLatest && <small>최신</small>}</div>
            <div className="nodeDashboard__recordDate"><CalendarDays size={17} aria-hidden="true" /><strong>{dateLabel(current)}</strong></div>
            <div className="nodeDashboard__recordFile" title={current.file_name}><FileText size={14} aria-hidden="true" /><span>{current.file_name}</span></div>
          </div>
          <div className="nodeDashboard__recordResult"><span>파일 진단 결과</span><Result file={current} /></div>
        </div>
        <label className="nodeDashboard__recordSelect"><span className="nodeDashboard__recordSelectLabel"><ListFilter size={15} aria-hidden="true" />측정 기록 선택</span>
          <span className="nodeDashboard__recordSelectControl"><CalendarDays className="nodeDashboard__recordSelectIcon" size={16} aria-hidden="true" />
            <select value={current.upload_id} onChange={event => setSelection(event.target.value)}>{[...records].reverse().map(file => <option key={file.upload_id} value={file.upload_id}>{dateLabel(file)} · {file.file_name}</option>)}</select>
            <ChevronDown className="nodeDashboard__recordSelectArrow" size={16} aria-hidden="true" />
          </span>
        </label>
        <dl className="nodeDashboard__values">{shown.map(metric => {
            const change = previousChange(records, current, metric);
            const label = metric.id === "center" ? "온도" : metric.id === "ultrasound" ? "검출 초음파" : metric.label;
            return <div key={metric.id}><dt>{label}</dt><dd>{metricText(metric.value(current), metric.unit)}</dd>{change && <small title={"직전 유효 측정: " + dateLabel(change.previous)}>직전 대비 {change.value > 0 ? "+" : ""}{metricText(change.value, metric.unit)}</small>}</div>;
          })}</dl>
        <footer className="nodeDashboard__recordFooter"><span>저장된 측정값과 파일 진단 결과</span>{fileHref(projectId, current) && <Link className="nodeDashboard__fileLink" to={fileHref(projectId, current)}>파일 상세 보기 →</Link>}</footer>
      </section>
    </NodeDetails>
  </>;
}

export default function TreeNodeDashboard({ identity, projectId, node, nodes, refreshVersion }) {
  const [data, setData] = useState({ loading: true, files: [] });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setData({ loading: true, files: [] });
    getTreeDashboard(projectId, node.id, controller.signal).then(response => {
      if (!controller.signal.aborted) setData(response.success ? { loading: false, files: sortRecords(response.data?.files || []) }
        : { loading: false, files: [], error: response.message || "기록을 불러오지 못했습니다." });
    }).catch(() => { if (!controller.signal.aborted) setData({ loading: false, files: [], error: "기록을 불러오지 못했습니다." }); });
    return () => controller.abort();
  }, [projectId, node.id, refreshVersion, retry]);
  if (data.loading || data.error) return <div className="treeDashboard__summaryRow">{identity}<div className="treeDashboard__summaryGroup"><div className="nodeDashboard" aria-busy={data.loading}>
    {data.loading ? <p role="status" className="nodeDashboard__empty">선택한 항목의 측정 기록을 불러오는 중…</p>
      : <div role="alert" className="nodeDashboard__empty"><p>{data.error}</p><button type="button" onClick={() => setRetry(value => value + 1)}>다시 불러오기</button></div>}
  </div></div></div>;
  return node.node_type === "facility"
    ? <FacilityRecords identity={identity} projectId={projectId} node={node} nodes={nodes} records={data.files}
      detailsStorageKey={`project_tree_node_details_v1:${projectId}:${node.id}`} />
    : <PartRecords identity={identity} projectId={projectId} records={data.files}
      detailsStorageKey={`project_tree_node_details_v1:${projectId}:${node.id}`} />;
}
